import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Fade,
  Slide,
  Avatar,
} from "@mui/material";
import {
  ArrowForward,
  CheckCircle,
  Refresh,
  ErrorOutline,
  LockOpen,
  Logout,
} from "@mui/icons-material";
import {
  storeDivision,
  removeDivision,
  removeToken,
} from "../../Services/LocalStorageService";
import { DivisionList } from "../../Services/Services";

// ─── Keyframes ───
const injectKeyframes = () => {
  if (document.getElementById("division-keyframes")) return;
  const style = document.createElement("style");
  style.id = "division-keyframes";
  style.textContent = `
    @keyframes fadeInScale {
      0% { opacity: 0; transform: scale(0.9); }
      100% { opacity: 1; transform: scale(1); }
    }
    @keyframes slideUp {
      0% { opacity: 0; transform: translateY(20px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes borderPulse {
      0%, 100% { border-color: rgba(250, 140, 27, 0.3); }
      50% { border-color: rgba(250, 140, 27, 0.8); }
    }
    @keyframes orangeGlow {
      0%, 100% { box-shadow: 0 0 20px rgba(250, 140, 27, 0.2); }
      50% { box-shadow: 0 0 40px rgba(250, 140, 27, 0.4); }
    }
    @keyframes cardHover {
      0% { transform: translateY(0); }
      100% { transform: translateY(-8px); }
    }
  `;
  document.head.appendChild(style);
};

const DivisionPage = () => {
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    injectKeyframes();
    removeDivision();
    fetchDivisions();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const hasDivision = localStorage.getItem("selectedDivision");
      if (!hasDivision) {
        window.history.pushState(null, "", "/division-cards");
      }
    };

    window.history.pushState(null, "", "/division-cards");
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const fetchDivisions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await DivisionList();
      setDivisions(response.data || response);
    } catch (err) {
      console.error("Error fetching divisions:", err);
      setError("Failed to load divisions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCardSelect = (division) => {
    setShowWarning(false);
    setSelectedDivision((prev) =>
      prev?.division_id === division.division_id ? null : division
    );
  };

  const handleNext = () => {
    if (!selectedDivision) {
      setShowWarning(true);
      setTimeout(() => setShowWarning(false), 3000);
      return;
    }

    storeDivision(selectedDivision);
    localStorage.setItem("selectedDivision", JSON.stringify(selectedDivision));

    navigate("/dashboard", {
      state: { division: selectedDivision },
      replace: true,
    });
  };

  // Orange color palette from login page
  const orangeColor = "rgb(250, 140, 27)";
  const orangeHover = "#f9a51cff";
  const orangeLight = "rgba(250, 140, 27, 0.1)";
  const orangeMedium = "rgba(250, 140, 27, 0.5)";

  const avatarColors = [
    "linear-gradient(135deg, #fa8c1b 0%, #f9a51c 100%)",
    "linear-gradient(135deg, #f9a51c 0%, #fa8c1b 100%)",
    "linear-gradient(135deg, #fa8c1b 0%, #f9b84c 100%)",
    "linear-gradient(135deg, #f9b84c 0%, #fa8c1b 100%)",
    "linear-gradient(135deg, #fa8c1b 0%, #ff8c42 100%)",
    "linear-gradient(135deg, #ff8c42 0%, #fa8c1b 100%)",
  ];

  // ─── Logout Button ───
  const LogoutButton = () => (
    <Box
      sx={{
        position: "fixed",
        top: 20,
        left: 20,
        zIndex: 2000,
      }}
    >
      <Button
        startIcon={<Logout />}
        onClick={() => {
          removeToken();
          navigate("/", { replace: true });
        }}
        sx={{
          color: "#fff",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.85rem",
          borderRadius: 0,
          px: 2.5,
          py: 1,
          border: "1px solid rgba(255,255,255,0.2)",
          backdropFilter: "blur(10px)",
          background: "rgba(0,0,0,0.3)",
          transition: "all 0.3s ease",
          "&:hover": {
            background: orangeLight,
            borderColor: orangeColor,
            color: orangeColor,
            transform: "translateX(-3px)",
          },
        }}
      >
        Logout
      </Button>
    </Box>
  );

  // ─── LOADING ───
  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          background: "linear-gradient(rgba(44, 43, 43, 0.9), rgba(35, 31, 31, 0.95))",
        }}
      >
        <LogoutButton />
        <Box sx={{ position: "relative" }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{ 
              color: orangeLight,
              position: "absolute",
              left: 0,
            }}
          />
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: orangeColor,
              animationDuration: "800ms",
            }}
          />
        </Box>
        <Typography 
          sx={{ 
            color: "#fff", 
            fontWeight: 500,
            letterSpacing: "1px",
          }}
        >
          Loading divisions...
        </Typography>
      </Box>
    );
  }

  // ─── ERROR ───
  if (error) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          p: 3,
          background: "linear-gradient(rgba(44, 43, 43, 0.9), rgba(35, 31, 31, 0.95))",
        }}
      >
        <LogoutButton />
        <ErrorOutline sx={{ fontSize: 64, color: orangeColor }} />
        <Alert
          severity="error"
          sx={{
            borderRadius: 0,
            maxWidth: 420,
            bgcolor: "rgba(0,0,0,0.5)",
            border: `1px solid ${orangeColor}`,
            color: "#fff",
          }}
          action={
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={fetchDivisions}
              sx={{ 
                color: orangeColor,
                "&:hover": {
                  bgcolor: orangeLight,
                }
              }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // ─── MAIN ───
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(rgba(44, 43, 43, 0.95), rgba(35, 31, 31, 0.98))",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Fixed Logout Button */}
      <LogoutButton />

      {/* Header - Fixed at top */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            textAlign: "center",
            // pt: { xs: 8, sm: 10 },
            pb: 2,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "#fff",
              mb: 1,
              fontSize: { xs: "1rem", sm: "2.2rem" },
            }}
          >
            Select Your Division
          </Typography>
  
        </Box>
      </Fade>

      {/* ══════════ WARNING SNACKBAR ══════════ */}
      <Slide direction="down" in={showWarning} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            top: 80,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2000,
          }}
        >
          <Alert
            severity="warning"
            sx={{
              borderRadius: 0,
              boxShadow: `0 4px 20px ${orangeMedium}`,
              fontWeight: 600,
              bgcolor: orangeColor,
              color: "#fff",
              border: "none",
            }}
            icon={<LockOpen sx={{ color: "#fff" }} />}
          >
            Please select a division to continue
          </Alert>
        </Box>
      </Slide>

      {/* ══════════ CARDS GRID - Scrollable area ══════════ */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          maxWidth: 1300,
          mx: "auto",
          mt:5,
          px: { xs: 2, sm: 3, lg: 4 },
          width: "100%",
          position: "relative",
          // Custom scrollbar styling
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(0,0,0,0.2)",
          },
          "&::-webkit-scrollbar-thumb": {
            background: orangeMedium,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: orangeColor,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 3,
            pb: 2,
          }}
        >
          {divisions.map((division, index) => {
            const isSelected =
              selectedDivision?.division_id === division.division_id;
            const isHovered = hoveredCard === division.division_id;
            const colorIndex = index % avatarColors.length;

            return (
              <Box
                key={division.division_id}
                sx={{
                  animation: `fadeInScale 0.5s ease ${index * 0.1}s both`,
                }}
              >
                <Card

                  elevation={0}
                  onClick={() => handleCardSelect(division)}
                  onMouseEnter={() => setHoveredCard(division.division_id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    borderRadius: 0,
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    background: isSelected
                      ? "linear-gradient(135deg, rgba(250,140,27,0.15) 0%, rgba(249,165,28,0.1) 100%)"
                      : "rgba(0,0,0,0.5)",
                    backdropFilter: "blur(10px)",
                    border: isSelected 
                      ? `2px solid ${orangeColor}`
                      : isHovered
                        ? `2px solid ${orangeMedium}`
                        : "2px solid rgba(255,255,255,0.1)",
                    transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                    transform: isSelected
                      ? "translateY(-4px)"
                      : isHovered
                        ? "translateY(-2px)"
                        : "translateY(0)",
                    boxShadow: isSelected
                      ? `0 10px 30px ${orangeMedium}`
                      : isHovered
                        ? "0 10px 30px rgba(0,0,0,0.3)"
                        : "0 4px 15px rgba(0,0,0,0.2)",
                    "&:hover": {
                      borderColor: orangeMedium,
                    },
                  }}
                >
                  {/* Selected checkmark */}
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        zIndex: 10,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: orangeColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 2px 10px ${orangeColor}`,
                      }}
                    >
                      <CheckCircle sx={{ color: "#fff", fontSize: 18 }} />
                    </Box>
                  )}

                  <CardContent
                    sx={{
                      p: 4,
                      
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 20,
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 0,
                        fontWeight: 800,
                        fontSize: "1.5rem",
                        background: avatarColors[colorIndex],
                        boxShadow: isSelected
                          ? `0 4px 15px ${orangeColor}`
                          : "0 4px 10px rgba(0,0,0,0.3)",
                        transition: "all 0.3s ease",
                        transform: isHovered
                          ? "scale(1.1)"
                          : "scale(1)",
                        mb: 2,
                      }}
                    >
                      {division.division.charAt(0).toUpperCase()}
                    </Avatar>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        textAlign: "center",
                        color: isSelected
                          ? orangeColor
                          : "#fff",
                        transition: "color 0.3s ease",
                      }}
                    >
                      {division.division}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>

        {/* Division count */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: "center", mt: 2, pb: 2 }}>
            <Chip
              label={`${divisions.length} divisions available`}
              size="small"
              sx={{
                bgcolor: "rgba(0,0,0,0.5)",
                color: "rgba(255,255,255,0.6)",
                border: `1px solid ${orangeLight}`,
                fontSize: "0.75rem",
                borderRadius: 0,
              }}
            />
          </Box>
        </Fade>
      </Box>

      {/* ══════════ BOTTOM BAR - Fixed at bottom ══════════ */}
      <Box
        sx={{
          flexShrink: 0,
          background: "rgba(0,0,0,0.8)",
          backdropFilter: "blur(10px)",
          borderTop: `1px solid ${orangeLight}`,
          px: { xs: 2, sm: 4 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left: Selected info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {selectedDivision ? (
            <Fade in>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircle sx={{ color: orangeColor, fontSize: 20 }} />
                <Typography
                  sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.9rem" }}
                >
                  Selected:{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: orangeColor }}
                  >
                    {selectedDivision.division}
                  </Box>
                </Typography>
              </Box>
            </Fade>
          ) : (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.9rem",
                fontStyle: "italic",
              }}
            >
              No division selected
            </Typography>
          )}
        </Box>

        {/* Right: Next button */}
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForward />}
          onClick={handleNext}
          disabled={!selectedDivision}
          sx={{
            background: selectedDivision ? orangeColor : "rgba(255,255,255,0.1)",
            borderRadius: 0,
            px: { xs: 3, sm: 4 },
            py: 1.2,
            fontSize: "0.95rem",
            fontWeight: 700,
            textTransform: "none",
            color: selectedDivision ? "#fff" : "rgba(255,255,255,0.3)",
            border: selectedDivision
              ? `1px solid ${orangeColor}`
              : "1px solid rgba(255,255,255,0.1)",
            boxShadow: selectedDivision
              ? `0 4px 15px ${orangeMedium}`
              : "none",
            transition: "all 0.3s ease",
            "&:hover": {
              background: selectedDivision ? orangeHover : "rgba(255,255,255,0.15)",
              transform: selectedDivision ? "translateY(-2px)" : "none",
              boxShadow: selectedDivision
                ? `0 8px 25px ${orangeColor}`
                : "none",
            },
            "&:disabled": {
              color: "rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.05)",
            },
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default DivisionPage;